import "./stylesheet/customLogin.css";

import {
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import React, { Component } from "react";
import {
  confirmSignIn,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  setUpTOTP,
  signIn,
  signInWithRedirect,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
} from "@aws-amplify/auth";
import CONFIG from "../../config";
import Alert from "@mui/material/Alert";
import { send_request } from "../../utils/Request";
import ThemeLogo from "./ThemeLogo";
import QRCode from "qrcode";

export class CustomSignIn extends Component {
  constructor(props) {
    super(props);
    this._validAuthStates = ["signIn", "signedOut", "signedUp"];
    this.state = {
      username: "",
      password: "",
      errorMessage: "",
      openError: false,
      formState: "signIn",
      loading: false,
      user: null,
      timezone: null,
      status: false,
      confirmMessage: null,
      organisationNameCheck: false,
      organisationName: "",
      firstName: "",
      firstNameCheck: false,
      surName: "",
      accountsLinked: false,
      totpCode: "",
      totpSecret: "",
      totpSetupUri: "",
      totpQrCodeDataUrl: "",
      mfaChallengeCode: "",
      mfaChallengeSubmitting: false,
      mfaSetupSubmitting: false,
    };
    this.postMfaCompletion = null;
    this.totpSetupDetails = null;
    this.otpInputRefs = {
      mfaChallengeCode: [],
      totpCode: [],
    };
  }

  componentDidMount = async () => {
    let confirmMessage = sessionStorage.getItem("confirmMessage");
    let errorMessage = sessionStorage.getItem("errorMessage");
    let openError = sessionStorage.getItem("openError");
    this.setState({
      username: "",
      password: "",
      formState: "signIn",
      confirmMessage: confirmMessage,
      openError: openError,
      errorMessage,
    });

    if (String(window.location.hash).indexOf("AccountsLinked.") != -1) {
      this.setState({ accountsLinked: true });
      window.location.hash = "";
    }

    try {
      const user = await getCurrentUser();
      if (user) {
        this.checkUser(user);
      }
    } catch (e) {
      console.log(e);
      this.props.onStateChange("signedOut", {});
      console.log("Not signed in");
    }
  };

  handleFormSubmission = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (this.state.formState === "signIn") {
      this.signIn();
    } else if (this.state.formState === "changePassword") {
      this.changePassword();
    } else if (this.state.formState === "verifyTotpChallenge") {
      this.confirmTotpChallenge();
    } else if (this.state.formState === "setupTotp") {
      this.verifyTotpSetupCode();
    }
  };

  /**
   * Completes the password change for invited users with temporary passwords.
   * Confirms sign-in by setting a new password and updates the user's authentication state.
   */
  changePassword = async () => {
    try {
      // Extract the new password from the component's state
      const { newChangePassword } = this.state;

      // Step 1: Confirm sign-in with the new password
      const response = await confirmSignIn({
        challengeResponse: newChangePassword,
      });

      if (this.isTotpChallenge(response)) {
        this.setState({ formState: "verifyTotpChallenge", loading: false });
        return;
      }

      if (response.isSignedIn) {
        await this.checkUser(await getCurrentUser());
        this.updateLastLogged();
      } else {
        throw new Error("Password change was unsuccessful.");
      }
    } catch (error) {
      console.error("Password change failed:", error);
      this.setState({ openError: true, errorMessage: error.message });
    }
  };

  isSoftwareTokenMfaEnabled = (mfaPreference) => {
    if (!mfaPreference) {
      return false;
    }

    const preferred =
      mfaPreference.preferred || mfaPreference.preferredMFA || "";
    const enabled =
      mfaPreference.enabled || mfaPreference.enabledMFATypes || [];

    const enabledTypes = Array.isArray(enabled) ? enabled : [enabled];
    const normalizedPreferred = String(preferred).toUpperCase();
    const normalizedEnabled = enabledTypes.map((type) =>
      String(type).toUpperCase(),
    );

    return (
      normalizedPreferred.includes("SOFTWARE_TOKEN_MFA") ||
      normalizedPreferred.includes("TOTP") ||
      normalizedEnabled.some(
        (type) => type.includes("SOFTWARE_TOKEN_MFA") || type.includes("TOTP"),
      )
    );
  };

  isTotpChallenge = (signInResult) => {
    const step = signInResult?.nextStep?.signInStep;
    const challengeName = signInResult?.ChallengeName;
    return (
      step === "CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA" ||
      step === "CONFIRM_SIGN_IN_WITH_TOTP_CODE" ||
      challengeName === "SOFTWARE_TOKEN_MFA"
    );
  };

  getOtpDigits = (value) => {
    const sanitized = (value || "").replace(/\D/g, "").slice(0, 6);
    return Array.from({ length: 6 }, (_, i) => sanitized[i] || "");
  };

  setOtpValue = (fieldName, value) => {
    this.setState({
      [fieldName]: (value || "").replace(/\D/g, "").slice(0, 6),
    });
  };

  focusOtpInput = (fieldName, index) => {
    const ref = this.otpInputRefs?.[fieldName]?.[index];
    if (ref && typeof ref.focus === "function") {
      ref.focus();
      ref.select?.();
    }
  };

  handleOtpDigitChange = (fieldName, index, event) => {
    const nextChar = (event.target.value || "").replace(/\D/g, "").slice(-1);
    const digits = this.getOtpDigits(this.state[fieldName]);
    digits[index] = nextChar;
    const newValue = digits.join("");
    this.setOtpValue(fieldName, newValue);
    if (nextChar && index < 5) {
      this.focusOtpInput(fieldName, index + 1);
    }
    // Auto-submit when all 6 digits are entered
    if (newValue.length === 6 && /^\d{6}$/.test(newValue)) {
      setTimeout(() => this.handleFormSubmission(), 0);
    }
  };

  handleOtpKeyDown = (fieldName, index, event) => {
    if (event.key === "Backspace") {
      const digits = this.getOtpDigits(this.state[fieldName]);
      if (!digits[index] && index > 0) {
        this.focusOtpInput(fieldName, index - 1);
      }
    }
    if (event.key === "ArrowLeft" && index > 0) {
      this.focusOtpInput(fieldName, index - 1);
    }
    if (event.key === "ArrowRight" && index < 5) {
      this.focusOtpInput(fieldName, index + 1);
    }
  };

  handleOtpPaste = (fieldName, event) => {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    this.setOtpValue(fieldName, pasted);
    this.focusOtpInput(fieldName, Math.min(pasted.length, 6) - 1);
    // Auto-submit when pasted code is 6 digits
    if (pasted.length === 6) {
      setTimeout(() => this.handleFormSubmission(), 0);
    }
  };

  renderOtpInputs = (fieldName, disabled = false) => {
    const digits = this.getOtpDigits(this.state[fieldName]);
    return (
      <div className="mfa-otp-container">
        {digits.map((digit, index) => (
          <TextField
            key={`${fieldName}-${index}`}
            value={digit}
            onChange={(event) =>
              this.handleOtpDigitChange(fieldName, index, event)
            }
            onKeyDown={(event) =>
              this.handleOtpKeyDown(fieldName, index, event)
            }
            onPaste={(event) => this.handleOtpPaste(fieldName, event)}
            inputRef={(ref) => {
              this.otpInputRefs[fieldName][index] = ref;
            }}
            disabled={disabled}
            variant="outlined"
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              pattern: "[0-9]*",
              autoComplete: "one-time-code",
            }}
          />
        ))}
      </div>
    );
  };

  requiresTotpSetup = async () => {
    try {
      const mfaPolicy = await send_request(
        `authz-service/mfa-enforcement`,
        "",
        {},
        "GET",
      );
      if (!mfaPolicy?.data?.mfaEnforced) {
        return false;
      }

      const mfaPreference = await fetchMFAPreference();
      return !this.isSoftwareTokenMfaEnabled(mfaPreference);
    } catch (error) {
      return false;
    }
  };

  beginTotpSetup = async () => {
    try {
      const details = await setUpTOTP();
      const setupUri = details?.getSetupUri
        ? details.getSetupUri("Workflow86")
        : "";
      const totpQrCodeDataUrl = setupUri
        ? await QRCode.toDataURL(setupUri, { width: 240, margin: 1 })
        : "";

      this.totpSetupDetails = details;
      this.setState({
        formState: "setupTotp",
        totpSecret: details?.sharedSecret || "",
        totpSetupUri: setupUri,
        totpQrCodeDataUrl,
        totpCode: "",
        loading: false,
      });
    } catch (error) {
      this.setState({
        openError: true,
        errorMessage: error.message || "Unable to initialize MFA setup.",
        loading: false,
      });
    }
  };

  completeSignInWithMfaGate = async (onComplete) => {
    const shouldSetupTotp = await this.requiresTotpSetup();
    if (shouldSetupTotp) {
      this.postMfaCompletion = onComplete;
      await this.beginTotpSetup();
      return;
    }

    await onComplete();
  };

  verifyTotpSetupCode = async () => {
    if ((this.state.totpCode || "").length !== 6) {
      this.setState({
        openError: true,
        errorMessage: "Enter all 6 digits from your authenticator app.",
      });
      return;
    }

    this.setState({ mfaSetupSubmitting: true });
    try {
      await verifyTOTPSetup({ code: this.state.totpCode });
      await updateMFAPreference({ sms: "DISABLED", totp: "PREFERRED" });
      this.setState({
        totpQrCodeDataUrl: "",
        totpSetupUri: "",
        totpSecret: "",
        totpCode: "",
      });

      if (this.postMfaCompletion) {
        const completion = this.postMfaCompletion;
        this.postMfaCompletion = null;
        await completion();
      } else {
        this.props.onStateChange("signedIn", {});
      }
    } catch (error) {
      this.setState({
        openError: true,
        errorMessage: error.message || "Unable to verify MFA code.",
      });
    } finally {
      this.setState({ mfaSetupSubmitting: false });
    }
  };

  confirmTotpChallenge = async () => {
    if ((this.state.mfaChallengeCode || "").length !== 6) {
      this.setState({
        openError: true,
        errorMessage: "Enter all 6 digits to continue.",
      });
      return;
    }

    this.setState({ mfaChallengeSubmitting: true });
    try {
      const result = await confirmSignIn({
        challengeResponse: this.state.mfaChallengeCode,
      });
      if (result.isSignedIn) {
        await this.checkUser(await getCurrentUser());
        this.updateLastLogged();
      }
    } catch (error) {
      this.setState({
        openError: true,
        errorMessage: error.message || "The MFA code is invalid.",
      });
    } finally {
      this.setState({ mfaChallengeSubmitting: false });
    }
  };

  checkClientName = () => {
    const { organisationName } = this.state;

    if (organisationName) {
      if (organisationName.trim() === "") {
        this.setState({
          organisationNameCheck: true,
        });
        return true;
      } else {
        this.setState({
          organisationNameCheck: false,
        });
        return false;
      }
    } else {
      this.setState({
        organisationNameCheck: true,
      });
      return true;
    }
  };

  signIn = async () => {
    sessionStorage.clear();
    this.setState({
      formState: "signIn",
      loading: true,
    });
    const username = this.state.username;
    const password = this.state.password;

    // Staging lockdown: only allow specific test accounts
    const allowedUsers = import.meta.env.VITE_ALLOWED_USERS;
    if (allowedUsers) {
      const allowed = allowedUsers
        .split(",")
        .map((e) => e.trim().toLowerCase());
      if (!allowed.includes(username.trim().toLowerCase())) {
        this.setState({
          openError: true,
          errorMessage:
            "Access restricted. This staging environment is locked to authorized test accounts only.",
          loading: false,
        });
        return;
      }
    }

    if (password == "") {
      this.setState({
        openError: true,
        errorMessage: "Password cannot be empty.",
        loading: false,
      });
    } else {
      try {
        const { isSignedIn, nextStep } = await signIn({ username, password });
        if (
          nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          this.props.firstLogin(true);
          this.setState({
            formState: "changePassword",
            user: nextStep.signInStep,
          });
        } else if (this.isTotpChallenge({ nextStep })) {
          this.setState({
            formState: "verifyTotpChallenge",
            loading: false,
          });
        } else if (isSignedIn) {
          await this.checkUser(await getCurrentUser());
          this.updateLastLogged();
        } else {
          this.setState({
            openError: true,
            errorMessage: `Unsupported sign-in step: ${nextStep?.signInStep || "unknown"}`,
            loading: false,
          });
        }
      } catch (error) {
        if (error.name === "UserLambdaValidationException") {
          this.setState({
            openError: true,
            errorMessage: "This email is already being used by another account",
            loading: false,
          });
        } else if (error.message === "User is not confirmed.") {
          this.setState({
            openError: true,
            errorMessage:
              "Your account has not been confirmed yet. Please check your email for a confirmation link.",
            loading: false,
          });
        } else {
          this.setState({
            openError: true,
            errorMessage: error.message,
            loading: false,
          });
        }
      }
    }
  };

  checkUser = async (user) => {
    let email;

    const attributes = await fetchUserAttributes();
    email = attributes.email;

    // Staging lockdown: verify the authenticated user's email is in the allowlist
    const allowedUsers = import.meta.env.VITE_ALLOWED_USERS;
    if (allowedUsers) {
      const allowed = allowedUsers
        .split(",")
        .map((e) => e.trim().toLowerCase());
      if (!allowed.includes((email || "").trim().toLowerCase())) {
        await signOut();
        this.setState({
          openError: true,
          errorMessage:
            "Access restricted. This staging environment is locked to authorized test accounts only.",
          loading: false,
          formState: "signIn",
        });
        return;
      }
    }

    const userInfo = {
      email: email,
      timezone: this.state.timezone,
    };

    let requestError = false;

    const url = `authz-service/check-user-verified-and-completed`;
    const json = await send_request(url, "", "", "POST").catch((err) => {
      requestError = true;
    });

    if (requestError || !json || !json.data) {
      await signOut();
      const errMessage =
        "An error was encountered when signing you in. Please try again";
      this.setState({
        loading: false,
        openError: true,
        errorMessage: errMessage,
      });
      return;
    }

    if (json.status == "200" && json.data.verified === false) {
      const createURL = `authz-service/create-new-user-and-client`;
      await send_request(createURL, userInfo, "", "POST")
        .then(async () => {
          this.props.firstLogin(true);
          this.props.onStateChange("signIn", {});
        })
        .catch((err) => {
          throw Error(err.message);
        });
    } else {
      if (json.data && json.data.isCompleted) {
        await this.completeSignInWithMfaGate(async () => {
          this.props.onStateChange("signedIn", {});
        });
      } else {
        this.props.firstLogin(true);
        this.props.onStateChange("signIn", {});
        this.getUserInfo();
      }
    }
  };

  getUserInfo = async () => {
    let firstName = "";
    await getCurrentUser().then(async (user) => {
      if (user) {
        const { attributes } = await fetchUserAttributes(user);
        if (attributes && attributes.given_name) {
          firstName = attributes.given_name;
        }
      }
    });

    await send_request(`authz-service/user-info`, "", {}).then((res) => {
      if (res.data) {
        this.setState({
          organisationName: res.data.client ? res.data.client.clientName : "",
          timezone: res.data.client ? res.data.client.timezone : null,
          firstName: res.data.firstName ? res.data.firstName : firstName,
          surName: res.data.lastName,
        });
      }
    });
  };

  updateLastLogged = async () => {
    const json = await send_request(`authz-service/update-last-logged`, "", {});
  };

  handleClose = () => {
    this.setState({ openError: false });
  };

  backToSignIn = () => {
    this.setState({ formState: "signIn" });
  };

  handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const check_type = ["radio", "checkbox"].includes(type);

    // Update state
    let updatedState = { checkedValue: check_type ? value : null };
    updatedState[name] = check_type ? checked : value;
    this.setState(updatedState);
  };

  signInWithGG = async () => {
    // Staging lockdown: block Google OAuth when allowlist is set
    const allowedUsers = import.meta.env.VITE_ALLOWED_USERS;
    if (allowedUsers) {
      this.setState({
        openError: true,
        errorMessage:
          "Google sign-in is disabled on this staging environment. Please use email/password.",
      });
      return;
    }

    try {
      await signInWithRedirect({
        provider: "Google",
      });
    } catch (e) {
      console.log(e);
    }
  };

  render() {
    const { formState, confirmMessage, errorMessage } = this.state;

    return (
      <React.Fragment>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={this.state.openError}
          autoHideDuration={6000}
          onClose={this.handleClose}
          key={"top" + "center"}
        >
          <Alert onClose={this.handleClose} severity="error" variant="filled">
            {errorMessage}
          </Alert>
        </Snackbar>
        {this.state.accountsLinked && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={this.state.accountsLinked}
            autoHideDuration={3000}
            onClose={() => {
              this.setState({ accountsLinked: false });
            }}
            key={"top" + "center"}
          >
            <Alert
              onClose={() => {
                this.setState({ accountsLinked: false });
              }}
              severity="success"
              variant="filled"
            >
              {
                "Your Google account is now linked to your Workflow86 account. Sign in again to continue."
              }
            </Alert>
          </Snackbar>
        )}

        {formState === "signIn" && (
          <div className="mainDiv">
            {this._validAuthStates.includes(this.props.authState) && (
              <form onSubmit={this.handleFormSubmission}>
                <Typography component="div">
                  <div>
                    <ThemeLogo />
                  </div>

                  <h4>
                    {confirmMessage
                      ? confirmMessage
                      : "Sign in to your account"}
                  </h4>
                </Typography>
                <div className="inputArea">
                  <p className="inputDescription">
                    Email or Username
                  </p>
                  <TextField
                    id="username"
                    key="username"
                    name="username"
                    style={{ width: "100%" }}
                    placeholder="Enter your email or username"
                    variant="outlined"
                    type="text"
                    onChange={this.handleInputChange}
                  />
                  <div className="forgotUsernamePasswordText">
                    <Typography>
                      <Link to="/forgotUsername">
                        Forgot your username?
                      </Link>
                    </Typography>
                  </div>
                  <p className="inputDescription">Password</p>
                  <TextField
                    style={{ width: "100%" }}
                    placeholder="Enter your password"
                    variant="outlined"
                    id="password"
                    key="password"
                    name="password"
                    type="password"
                    onChange={this.handleInputChange}
                  />
                </div>
                <div className="forgotUsernamePasswordText">
                  <Typography>
                    <Link to="/forgotPassword">
                      Forgot your password?
                    </Link>
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                    }}
                    className="flexStartMobile"
                  >
                    <br />
                    <Button
                      className="signInButton"
                      type="submit"
                      onClick={this.handleFormSubmission}
                      disabled={this.state.loading}
                      variant="contained"
                      color="primary"
                      id="sign_in_button"
                      fullWidth
                      style={{ marginTop: "16px" }}
                    >
                      Sign In
                      {this.state.loading && (
                        <CircularProgress
                          size={24}
                          className="buttonProgress"
                        />
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>
        )}

        {formState !== "signIn" && (
          <div className="mainDiv">
            {this._validAuthStates.includes(this.props.authState) && (
              <form onSubmit={this.handleFormSubmission}>
                {formState === "changePassword" ? (
                  <React.Fragment>
                    <Typography component="div">
                      <div>
                        <ThemeLogo />
                      </div>
                      <h4>Change Password</h4>
                    </Typography>
                    <div className="inputArea">
                      <p className="inputDescription">New Password</p>
                      <TextField
                        id="newChangePassword"
                        key="newChangePassword"
                        name="newChangePassword"
                        style={{ width: "100%" }}
                        placeholder="New Password"
                        variant="outlined"
                        type="password"
                        onChange={this.handleInputChange}
                      />
                      <p className={"s-text noWrap"}>
                        Must contain 6+ characters, special
                        character(s), number(s), uppercase and lower
                        case letter(s).
                      </p>
                      <p
                        className="inputDescription"
                        style={{ marginTop: "8px" }}
                      >
                        After submitting your new password, you will
                        be redirected back to the sign in page
                      </p>
                    </div>
                    <div className="actionArea">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          style={{ marginRight: "10px" }}
                          variant={"outlined"}
                          color={"info"}
                          onClick={this.backToSignIn}
                        >
                          {" "}
                          Back{" "}
                        </Button>
                        <Button
                          variant={"contained"}
                          color={"primary"}
                          type="submit"
                          onClick={this.handleFormSubmission}
                        >
                          {" "}
                          Change{" "}
                        </Button>
                      </div>
                    </div>
                  </React.Fragment>
                ) : null}
                {formState === "verifyTotpChallenge" ? (
                  <React.Fragment>
                    <Typography component="div">
                      <div>
                        <ThemeLogo />
                      </div>
                      <h4>Two-factor authentication</h4>
                      <p className="mfa-subtitle">
                        Enter the 6-digit code from your authenticator
                        app to continue.
                      </p>
                    </Typography>
                    <div className="inputArea">
                      {this.renderOtpInputs(
                        "mfaChallengeCode",
                        this.state.mfaChallengeSubmitting,
                      )}
                    </div>
                    <div className="mfa-actions">
                      <Button
                        variant={"outlined"}
                        onClick={this.backToSignIn}
                        disabled={this.state.mfaChallengeSubmitting}
                      >
                        Back
                      </Button>
                      <Button
                        variant={"contained"}
                        color={"primary"}
                        type="submit"
                        onClick={this.handleFormSubmission}
                        disabled={
                          this.state.mfaChallengeSubmitting ||
                          (this.state.mfaChallengeCode || "")
                            .length !== 6
                        }
                      >
                        {this.state.mfaChallengeSubmitting
                          ? "Verifying..."
                          : "Verify"}
                        {this.state.mfaChallengeSubmitting && (
                          <CircularProgress
                            size={18}
                            style={{ marginLeft: 8 }}
                            color="inherit"
                          />
                        )}
                      </Button>
                    </div>
                  </React.Fragment>
                ) : null}
                {formState === "setupTotp" ? (
                  <React.Fragment>
                    <Typography component="div">
                      <div>
                        <ThemeLogo />
                      </div>
                      <h4>Set up two-factor authentication</h4>
                      <p className="mfa-subtitle">
                        Scan the QR code below with your authenticator
                        app, then enter the verification code.
                      </p>
                    </Typography>
                    <div className="inputArea">
                      <div className="mfa-card">
                        {this.state.totpQrCodeDataUrl ? (
                          <div className="mfa-qr-wrapper">
                            <img
                              src={this.state.totpQrCodeDataUrl}
                              alt="MFA setup QR code"
                            />
                          </div>
                        ) : null}
                        <div className="mfa-divider">
                          <span>or enter manually</span>
                        </div>
                        <TextField
                          className="mfa-secret-field"
                          style={{ width: "100%" }}
                          value={this.state.totpSecret}
                          variant="outlined"
                          size="small"
                          disabled
                        />
                      </div>
                      <p
                        className="inputDescription"
                        style={{ marginTop: 16 }}
                      >
                        Verification code
                      </p>
                      {this.renderOtpInputs(
                        "totpCode",
                        this.state.mfaSetupSubmitting,
                      )}
                    </div>
                    <div className="mfa-actions">
                      <Button
                        variant={"outlined"}
                        onClick={this.backToSignIn}
                        disabled={this.state.mfaSetupSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant={"contained"}
                        color={"primary"}
                        type="submit"
                        onClick={this.handleFormSubmission}
                        disabled={
                          this.state.mfaSetupSubmitting ||
                          (this.state.totpCode || "").length !== 6
                        }
                      >
                        {this.state.mfaSetupSubmitting
                          ? "Verifying..."
                          : "Confirm"}
                        {this.state.mfaSetupSubmitting && (
                          <CircularProgress
                            size={18}
                            style={{ marginLeft: 8 }}
                            color="inherit"
                          />
                        )}
                      </Button>
                    </div>
                  </React.Fragment>
                ) : null}
              </form>
            )}
          </div>
        )}
      </React.Fragment>
    );
  }
}
